import { MatDialog } from '@angular/material/dialog';
import { SVG, Svg, Image, Element as SvgElement, ClipPath, G
       } from '@svgdotjs/svg.js';
import { CoordinateTranslation
       } from 'app/controls/image-cropper/coordinate-translation';
import { ImageQualityRatingService
       } from 'app/controls/image-quality-rating/image-quality-rating.service';
import { QualityRating
       } from 'app/controls/image-quality-rating/quality-rating';
import { injectMultiversionImageBehavior, loadUrl
       } from 'app/controls/multiversion-image/multiversion-image';
import { Choice, Button, ButtonsAssignment
       } from 'app/dialogs/dialog-buttons.component';
import { GalleryImage, GalleryService } from 'app/gallery/gallery.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { GalleryConstants, GlobalConstants, StageConstants
       } from 'core/constants';
import { TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Region } from 'core/geometry';
import { Globals } from 'core/globals';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { LoggingService } from 'core/logging/logging.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { TraudlService } from 'core/traudl/traudl.service';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { Subscription } from 'rxjs';
import { importImageStoreImage
       } from '../../../gallery/imageprovider';
import { ImageProvisioningService
       } from '../../../gallery/imageprovider.service';
import { CopyPlaceConfig } from '../../collage-designer-config';
import { CopyRules } from '../../copy-rules';
import { DesignStepService } from '../../design-step/design-step.service';
import { applyContentImageSwapping, enableImageDrag, DragDropGhostSVG,
         ImageDragGhostSVG, PlaceholderSource
       } from '../drag-drop';
import { Placeholder, PlaceholderType } from './placeholder';
import { PlaceholderContentService } from './placeholder-content.service';
import { PlaceholderService } from './placeholder.service';
import { TransformationManager } from './transformation-manager';

export interface BoundingBox
{
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Dimensions
{
  width: number;
  height: number;
}

interface ImageInformation
{
  id: string;
  galleryImage: GalleryImage;
  isUnchanged: boolean;
}

export class ImageLoadingError extends TrivialError
{
  // i18n.error.imageLoadingError.title
  // i18n.error.imageLoadingError.message
  protected _name: string = 'ImageLoadingError';

  public get buttons(): ButtonsAssignment
  {
    return {
      primary: Button.RETRY_PHOTO,
      secondary: Button.WITHOUT_PHOTO,
    };
  }
}

export class ImagePlaceholder extends Placeholder
{
  public placeholderHighlighter: G | undefined = undefined;
  public qualityIndicatorGroup: G | undefined = undefined;

  private static readonly OVERLAY_MASK_SIZE: number = 100000;
  private static readonly MAX_BOX_SIZE: number = 100; /* px */

  private _transformationManager: TransformationManager;
  private contentImageSwapping: DragDropGhostSVG;
  private imageDrag: ImageDragGhostSVG;
  private rootNode: Document | ShadowRoot = undefined;
  private readonly REPLACE_HIGHLIGHTER_IMAGE_PATH: string = Utils.url(
      './assets/img/replace-highlighter.svg');
  private readonly ADD_HIGHLIGHTER_IMAGE_PATH: string = Utils.url(
      './assets/img/add-highlighter.svg');
  private readonly eventListeners: Object = {
    'dragStart': this.dragStart.bind(this),
    'dragenter': this.dragEnter.bind(this),
    'dragover': Utils.eventPreventDefault,
    'dragleave': this.dragLeave.bind(this),
    'dragEnd': this.dragEnd.bind(this),
    'drop': this.dropImage.bind(this),
    'dropSwap': this.swapImage.bind(this),
    'click': this.imageClicked.bind(this),
    'mouseover': this.mouseOverImage.bind(this),
    'mouseleave': this.mouseLeave.bind(this)
  };
  private changeObserver?: MutationObserver;
  private sourcePrevSibling: Element;
  private clipPath: ClipPath;
  private imageOverlayShown: boolean = false;
  private overlayRelocation?:
      { container: G; parent: Node; prevSibling: Node | null };

  constructor(
      errorService: ErrorService,
      loggingService: LoggingService,
      designStepService: DesignStepService,
      galleryService: GalleryService,
      private imageQualityRatingService: ImageQualityRatingService,
      private imageProvisioningService: ImageProvisioningService,
      private imageStoreService: ImageStoreService,
      private traudlService: TraudlService,
      toolboxService: ToolboxService,
      placeholderContentService: PlaceholderContentService,
      placeholderService: PlaceholderService,
      eventTrackingService: EventTrackingService,
      productService: ProductService,
      image: Image,
      partId: string,
      isLocked: boolean,
      dialog: MatDialog)
  {
    super(
        errorService, loggingService, designStepService, galleryService,
        toolboxService, placeholderContentService, placeholderService,
        eventTrackingService, productService, image, partId,
        PlaceholderType.Image, isLocked, dialog);


    this.imageQualityRatingService = imageQualityRatingService;
    this.imageProvisioningService = imageProvisioningService;
    this.imageStoreService = imageStoreService;
    this.designStepService = designStepService;
    this.rootNode = this.element.node.getRootNode() as Document | ShadowRoot;
  }

  protected determineGroup(): G | undefined
  {
    const parent = this.wrapper.parent();
    return parent?.node.tagName === 'g' ? parent as G : undefined;
  }

  public async setup(): Promise<void>
  {
    injectMultiversionImageBehavior(this.element);
    this.element.load();

    if (this.zoomMode === StageConstants.ZOOM_MODE_CUT) {
      this.wrapper.attr('preserveAspectRatio', 'xMidYMid slice');
    }

    this.applyPendingListener();
    this.applyImageLoadedBehaviour();

    if (this.isContentChangeable()) {
      this.applyChangeableEventListeners();
      this.contentImageSwapping =
          applyContentImageSwapping(
            new PlaceholderSource(this, this.galleryService,
                                  this.placeholderService));

      if (!this.isLinked() && this.isUnchanged()) {
        await this.reset();
      }
      else if (!this.isLinked()) {
        const imageModel =
            this.getImageModel() || await this.tryGenerateModel(this.getHref());
        if (imageModel && !this.get(StageConstants.ATTRIBUTE_BOUNDING_BOX)) {
          await this.applyZoomMode(imageModel);
        }
      }
      this.setupBoundingBox(
          this.getBoundingBox(), this.getDimensions(), true);
    }

    if (this.isLinked()) {
      await this.createLinkedImage();
    }

    this.updateQualityRating();
  }

  public async destructor(): Promise<void>
  {
    await super.destructor();
    this.hidePlaceholderHighlighter();
    this.hideQualityIndication();
    if (this.isContentChangeable()) {
      this.removeChangeableEventListeners();
      this.contentImageSwapping.destructor();
    }
  }

  public isFlexible(): boolean
  {
    if (Globals.mode === GlobalConstants.MODE_GOD) {
      return true;
    }

    return false;
  }

  public hasEditableContent(): boolean
  {
    return this.getImageModel() !== undefined;
  }

  public async extractInformation(): Promise<ImageInformation | undefined>
  {
    const galleryImage = this.getImageModel();
    return galleryImage
      ? { id: this.get('id'),
          galleryImage: galleryImage,
          isUnchanged: this.isUnchanged(),
        }
      : undefined;
  }

  public async applyInformation(info: ImageInformation): Promise<void>
  {
    if (this.galleryService.isImageUsed(info.galleryImage.id)) {
      return;
    }

    await this.loadGalleryImage(info.galleryImage.id);
  }

  private applyPendingListener(): void
  {
    const observer = new MutationObserver(mutations => {
      if (this.isPending()) {
        this.showLoadingMarker();
        const subscription = this.galleryService.uploadFailed
          .subscribe((wrapper) => {
            this.hideLoadingMarker(wrapper);
            subscription.unsubscribe();
          });
      } else {
        this.hideLoadingMarker();
      }
    });

    observer.observe(this.element.node, {
        attributes: true,
        attributeFilter: [ StageConstants.ATTRIBUTE_PENDING ]
    });
  }

  private applyImageLoadedBehaviour(): void
  {
    this.element.on('error', this.error.bind(this));

    this.element.loaded = this.loaded.bind(this);
  }

  private applyChangeableEventListeners(): void
  {
    const image = this.element.node;

    this.changeObserver = new MutationObserver(mutations => {
      this.changed.emit(this.element.id());
    });
    this.changeObserver.observe(image, {attributes: true});

    for (const listener in this.eventListeners) {
      if (this.eventListeners.hasOwnProperty(listener)) {
        image.addEventListener(listener, this.eventListeners[listener]);
      }
    }
  }

  private removeChangeableEventListeners(): void
  {
    this.changeObserver?.disconnect();

    const image = this.element.node;
    for (const listener in this.eventListeners) {
      if (this.eventListeners.hasOwnProperty(listener)) {
        image.removeEventListener(listener, this.eventListeners[listener]);
      }
    }
  }

  private isDroppable(): boolean
  {
    return !this.isPending() &&
        this.galleryService.getDraggingImageId() &&
        (this.getImageModel() === undefined ||
         this.getImageModel().id !== this.galleryService.getDraggingImageId());
  }

  public async focus(): Promise<void>
  {
    await super.focus();
    if (!this.imageDrag) {
      this.imageDrag = enableImageDrag(
          new PlaceholderSource(
              this, this.galleryService, this.placeholderService));
    }
  }

  public blur(): void
  {
    super.blur();
    if (this.imageDrag) {
      this.imageDrag.destructor();
    }
  }

  private dragStart(event: Event): void
  {
    if (this.hasFocus()) {
      return;
    }
    this.showDropShroudMarker();
    this.showDropMarker();
  }

  private dragEnter(event: Event): void
  {
    if (this.galleryService.getDraggingImageId() && !this.isPending()) {
      this.showDragDropGhostSwapIcon();
      this.showDropMarker();
    }
  }

  private dragLeave(event: Event): void
  {
    this.hideDropMarker();
    this.hideDragDropGhostSwapIcon();
  }

  private dragEnd(event: Event): void
  {
    this.galleryService.setDraggingImageId(undefined);
    this.hideDropShroudMarker();
    this.hideDropMarker();
  }

  private async dropImage(event: Event): Promise<void>
  {
    event.preventDefault();
    this.hideDropMarker();

    const draggingImageId = this.galleryService.getDraggingImageId();
    if (draggingImageId !== undefined && this.isDroppable()) {
      await this.loadGalleryImage(draggingImageId);
      this.galleryService.setDraggingImageId(undefined);
    }
    this.clickEvent();
  }

  private async swapImage(event): Promise<void>
  {
    const shouldEditAfterDrop =
      !! this.placeholderService.getFocusedPlaceholder();
    this.galleryService.setSelectedImageId(undefined);
    this.placeholderService.setFocusedPlaceholder(undefined);
    this.hideDropMarker();

    if (!this.isDroppable()) {
      return;
    }

    await event.source.loadDraggingImage(this);

    this.galleryService.setSelectedImageId(undefined);
    this.galleryService.setDraggingImageId(undefined);

    if (shouldEditAfterDrop) {
      this.clickEvent();
    }
  }

  private async mouseOverImage(event: Event): Promise<void>
  {
    await super.mouseOver();
    if (!this.placeholderService.hasTransformHandle()) {
      this.wrapper.node.style.cursor = 'pointer';
    }
  }

  private showLoadingMarker(): void
  {
    if (this.wrapper
        .find(`.${StageConstants.CLASS_NAME_LOADING_MARKER}`)
        .at(0)) {
      return;
    }
    const box = this.wrapper.viewbox();

    const markerWidth = box.width / 4;
    const markerHeight = box.height / 4;

    this.wrapper
      .image()
      .load(Utils.url(GalleryConstants.LOADING_ICON_URL))
      .size(markerWidth, markerHeight)
      .move(box.x + (box.width - markerWidth) / 2,
            box.y + (box.height - markerHeight) / 2)
      .addClass(StageConstants.CLASS_NAME_LOADING_MARKER);

    this.element.addClass(
        StageConstants.CLASS_NAME_HIDE_PLACEHOLDER_THROUGH_OPACITY);
  }

  private hideLoadingMarker(target?: Svg): void
  {
    const wrapper = target ? target : this.wrapper;
    const marker = wrapper
      .find(`.${StageConstants.CLASS_NAME_LOADING_MARKER}`)
      .at(0);
    if (marker) {
      marker.remove();
    }

    this.element.removeClass(
        StageConstants.CLASS_NAME_HIDE_PLACEHOLDER_THROUGH_OPACITY);
  }

  private async createLinkedImage(): Promise<void>
  {
    this.detachContent();

    const currentPartId = this.designStepService.getPartId();
    const copyFrom = this.get(StageConstants.ATTRIBUTE_COPY_FROM);

    if (! copyFrom) {
      return;
    }

    let link = undefined;

    try {
      link = CopyRules.decodePlace(copyFrom, currentPartId);
    }
    catch (e) {
      this.errorService.add(e, 'PlaceholderImage');
      await this.errorService.flush('PlaceholderImage');
    }

    if (link.item === undefined) {
      this.fillWithStageSnapshot(link.part);
    }
    else {
      if (link.part === currentPartId) {
        const superiorImage =
            SVG(this.rootNode.getElementById(link.item)) as Image;
        if (! superiorImage) {
          return;
        }
        this.createMutationObserverForLinkedElement(superiorImage)
            .observe(superiorImage.node, {
                attributes: true,
                attributeOldValue: true,
                subtree: true
            });
      }
      else {
        await this.adoptItemFromAnotherPart(link);
     }
   }
 }

  private async adoptItemFromAnotherPart(link: CopyPlaceConfig): Promise<void>
  {
    const structure =
        await this.designStepService.getSVGStructureByPartId(link.part);
    if (!structure) {
      return;
    }
    const doc = (new DOMParser()).parseFromString(structure, 'image/svg+xml');

    const superiorImage = SVG(doc.getElementById(link.item)) as Image;
    if (!superiorImage) {
      return;
    }
    const superiorImageHref = superiorImage.attr(StageConstants.ATTRIBUTE_HREF);
    const superiorImageBBox = superiorImage.attr(
       StageConstants.ATTRIBUTE_BOUNDING_BOX);
    const superiorImageDimensions = superiorImage.attr(
        StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS);

    this.element.loaded = this.loaded.bind(this);

    this.element.load(superiorImageHref);

    const galleryImage = this.parseImageModel(superiorImage.attr(
        StageConstants.ATTRIBUTE_IMAGE_MODEL));
    if (galleryImage) {
      this.setImageModel(galleryImage);
      this.galleryService.setImageAsUsed(galleryImage.id);
    }

    this.setupBoundingBox(
        JSON.parse(superiorImageBBox), JSON.parse(superiorImageDimensions));

    this.removePlaceholderImageIfNecessary();
  }

  private renameConflictingElements(
    hostDoc: Element, embedded: Element): Element
  {
    const hostIds = StageUtils.listElementIds(hostDoc);
    const embeddedReferences = StageUtils.findReferencedElements(embedded);
    const conflicting =
      embeddedReferences.filter(it => hostIds.indexOf(it.referencedId) !== -1);

    for (const it of conflicting) {
      const conflictFreeId = `${it.referencedId}-${Utils.getRandomId()}`;
      it.reference.setAttribute('id', conflictFreeId);
      it.element.setAttribute(it.attribute, `url(#${conflictFreeId})`);
    }

    return embedded;
  }

  private giveClassesPrefix(svgText: string, prefix: string): string
  {
    const classes = (<any>Array.from((<any>svgText)
      .matchAll(/class="([^"]+)"/g)))
      .flatMap(m => m[1].split(/\s+/));

    const uniqueClasses = Array.from(new Set(classes));
    const map = (<any>Object).fromEntries(
        uniqueClasses.map(c => [c, prefix + '-' + c]));

    for (const [oldC, newC] of (<any>Object).entries(map)) {
      const classRegex = new RegExp(`\\b${oldC}\\b`, 'g');
      svgText = svgText.replace(classRegex, newC);
    }

    return svgText;
  }

  private async fillWithStageSnapshot(partId: string):
      Promise<void>
  {
    const partConfig = this.designStepService.getPartConfigByPartId(partId);

    const stageRotation =
        this.designStepService.getStageRotationByPartId(partId);

    // get selected template structure
    const structure =
        await this.designStepService.getSVGStructureByPartId(partId);
    if (!structure) {
      return;
    }

    // everything will be inserted into the current stage
    const targetSvg = this.element.root();

    // the structure will be transformed inside a temporary area,
    // because SVG.js does not work that well with detached documents.
    const doc = (new DOMParser()).parseFromString(
        this.giveClassesPrefix(
            StageUtils.sanitizeStructure(structure), this.wrapper.id()),
        'image/svg+xml'
    );

    const tempArea = targetSvg.nested();
    tempArea.node.appendChild(
      this.renameConflictingElements(
        targetSvg.node as Element,
        doc.firstChild as Element));

    const svg = SVG(tempArea.node.firstChild as SVGSVGElement);

    for (let prop in svg.attr()) {
      if (prop.startsWith('xmlns') && !targetSvg.hasOwnProperty(prop)) {
        targetSvg.attr(prop, svg.attr(prop));
      }
    }

    // Collect defs and add to own defs
    const targetDefs = targetSvg.defs();

    svg.find('defs').each(function(): void {
      for (const def of this.children()) {
        if (targetDefs.find('#' + def.id()).length === 0) {
          def.addTo(targetDefs);
        }
      }
    });

    // strip defs, metadata from document
    const stripElementByTag = (el: Svg, tag: string) => {
      el.find(tag).each(function (): void { this.remove(); });
    };

    stripElementByTag(svg, 'namedview');
    stripElementByTag(svg, 'metadata');
    stripElementByTag(svg, 'defs');

    // remove pap-attributes except pap:href
    const stripPapAttributes = (el: SvgElement) => {
      const whitelist = [StageConstants.ATTRIBUTE_HREF];

      for (const attrName of Object.keys(el.attr())) {
        if (attrName.startsWith('pap:') && whitelist.indexOf(attrName) === -1) {
          el.node.removeAttribute(attrName);
        }
      }
    };

    svg.find('*').each(function(): void {
      stripPapAttributes(this);
    });

    // apply viewbox
    if (partConfig.editingView.overlays.editableArea) {
      this.wrapper.viewbox(StageUtils.scaleEditableArea(
          svg.viewbox(), partConfig, stageRotation));
    }

    // clone elements into wrapper
    for (const c of svg.children()) {
      const cloned = c.clone();
      cloned.addTo(this.wrapper);
    }

    // remove original image
    this.element.remove();

    // mark wrapper as copy
    this.wrapper.attr(StageConstants.ATTRIBUTE_COPIED_FROM, `//${partId}`);
    this.wrapper.attr('preserveAspectRatio', 'none');

    const image =
        SVG(<any>this.wrapper.node.querySelector('image')) as Image;
    this.removePlaceholderImageIfNecessary(image);

    // remove temporary
    tempArea.remove();
  }

  private createMutationObserverForLinkedElement(superiorImage: Image):
      MutationObserver
  {
    const indexOfWithoutNamespace = (a, b) =>
        a.map((e) => { return e.attributeName.replace(/pap:/, ''); })
         .indexOf(b.replace(/pap:/, '')) !== -1;

    return new MutationObserver(mutations => {
      const superiorImageHref =
          superiorImage.attr(StageConstants.ATTRIBUTE_HREF);
      const imageHref = this.getHref();
      const superiorImageBBox =
          superiorImage.attr(StageConstants.ATTRIBUTE_BOUNDING_BOX);
      const imageBBox = this.get(StageConstants.ATTRIBUTE_BOUNDING_BOX);

      const imageChanged =
          indexOfWithoutNamespace(mutations, StageConstants.ATTRIBUTE_HREF) &&
          superiorImageHref !==
              superiorImage.attr(StageConstants.ATTRIBUTE_PLACEHOLDER_URL) &&
          superiorImageHref !== imageHref;

      const boundingBoxChanged = indexOfWithoutNamespace(mutations,
          StageConstants.ATTRIBUTE_BOUNDING_BOX) &&
          superiorImageHref !==
              superiorImage.attr(StageConstants.ATTRIBUTE_PLACEHOLDER_URL) &&
          superiorImageBBox !== imageBBox;

      const placeholderImageSet =
          indexOfWithoutNamespace(mutations, StageConstants.ATTRIBUTE_HREF) &&
          superiorImageHref ===
              superiorImage.attr(StageConstants.ATTRIBUTE_PLACEHOLDER_URL) &&
          imageHref !== superiorImageHref;

      this.element.loaded = this.loaded.bind(this);

      if (placeholderImageSet) {
        this.element.load(superiorImageHref);
      }

      if (imageChanged) {
        const galleryImage = this.parseImageModel(
          superiorImage.attr(StageConstants.ATTRIBUTE_IMAGE_MODEL));

        this.element.loaded = _ => this.loaded(galleryImage, true);

        loadUrl(this.element, superiorImageHref);
      }

      if (boundingBoxChanged) {
        const dimensions = superiorImage.attr(
            StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS);
        this.setupBoundingBox(
            JSON.parse(superiorImageBBox), JSON.parse(dimensions));
      }

      this.removePlaceholderImageIfNecessary();
    });
  }

  private removePlaceholderImageIfNecessary(_image?: Image): void
  {
    const image = _image ? _image : this.element;
    const isNecessary = !this.isContentChangeable() &&
        image.attr(StageConstants.ATTRIBUTE_HREF) &&
        image.attr(StageConstants.ATTRIBUTE_HREF) ===
            this.get(StageConstants.ATTRIBUTE_PLACEHOLDER_URL);

    if (isNecessary) {
      image.attr(StageConstants.ATTRIBUTE_HREF, '');
      image.node.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '');
    }
  }

  private async useDefaultCrop(): Promise<boolean>
  {
    // defaultCrop nur dann benutzen, wenn es sich bei dem aktuellen Template
    // um das Initiale oder ein davon abgeleitetes (fertig gestaltetes Produkt)
    // handelt.
    if (await this.designStepService.getSVGStructureUrl() ===
        this.designStepService.getInitialSVGStructureUrl() ||
        this.element.root().attr(StageConstants.ATTRIBUTE_INITIAL_STRUCTURE_URL)
            === this.designStepService.getInitialSVGStructureUrl()) {
      return true;
    }
    return false;
  }

  private async applyZoomMode(imageInfo: GalleryImage|any): Promise<void>
  {
    let boundingBox: BoundingBox;
    const dimensions = { width: imageInfo.width, height: imageInfo.height, };

    if (await this.useDefaultCrop()) {
      boundingBox = imageInfo.defaultCrop;
    }

    if (!boundingBox) {
      const wrapperRegion = Region.fromSimilar({
          x: 0,
          y: 0,
          width: this.wrapper.width() as number,
          height: this.wrapper.height() as number,
      });
      const imageRegion = Region.fromSimilar({
          x: 0,
          y: 0,
          width: imageInfo.width,
          height: imageInfo.height,
      });

      const translation = new CoordinateTranslation(
          wrapperRegion, imageRegion, imageInfo.contentCenter);
      boundingBox = this.zoomMode === StageConstants.ZOOM_MODE_CUT
          ? translation.regionToViewportSpace(wrapperRegion)
          : imageRegion;
    }

    this.setupBoundingBox(boundingBox, dimensions);
    this.element.width(imageInfo.width);
    this.element.height(imageInfo.height);
  }

  private setupBoundingBox(
      bbox: BoundingBox,
      dimensions: Dimensions,
      keepTransformation: boolean = false): void
  {
    if (!bbox || !dimensions) {
      return;
    }

    const matrix =
        keepTransformation ? this.getCurrentMatrix() : undefined;

    if (this._transformationManager === undefined) {
      this._transformationManager =
          new TransformationManager(dimensions, bbox, matrix);
    } else {
      this._transformationManager.reset(dimensions, bbox, matrix);
    }

    if (matrix === undefined) {
      this.element.node.removeAttribute('transform');
    }

    this.set(StageConstants.ATTRIBUTE_BOUNDING_BOX, JSON.stringify(bbox));
    this.set(
        StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS,
        JSON.stringify(dimensions));

    this.wrapper.viewbox(bbox);

    (<any>this.element).scheduleReload();
  }

  private applyCurrentMatrix(): void
  {
    this.element.node.setAttribute(
        'transform', this._transformationManager.getMatrix().toString());
  }

  private getCurrentMatrix(): DOMMatrixReadOnly|undefined
  {
    const node = this.element.node as unknown as SVGGraphicsElement;

    if (!node.hasAttribute('transform')) {
      return undefined;
    }

    const transform = node.transform.baseVal.consolidate();

    if (!transform) {
      return undefined;
    }

    const matrix = transform.matrix;

    return new DOMMatrixReadOnly(
        [matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f]);
  }

  public getMagnification(): number
  {
    return this._transformationManager === undefined
        ? 1
        : this._transformationManager.getMagnification();
  }

  public get transformationManager(): TransformationManager|undefined
  {
    return this._transformationManager;
  }

  public callWithTransformationManager(
      impl: (m: TransformationManager) => void): void
  {
    if (!this._transformationManager) {
      return;
    }

    impl(this._transformationManager);

    this.applyCurrentMatrix();
    (<any>this.element).scheduleReload();
  }

  private async loaded(imgInfo?: GalleryImage|any,
                       initial?: boolean): Promise<void>
  {
    if (initial && imgInfo) {
      await this.applyZoomMode(imgInfo);
    }

    this.updateQualityRating();
    this.changed.emit(this.element.id());
    this.element.loaded = () => {};
  }

  private async error(error: Error): Promise<void>
  {
    if (!this.isUnchanged()) {
      const imageUrl = this.element.node.getAttributeNS(
          'http://www.w3.org/1999/xlink', 'href');
      let trivialError = new ImageLoadingError(
          'image could not be loaded', [ imageUrl ]);

      this.errorService.add(trivialError, 'PlaceholderImage');
      const choice = await this.errorService.flush('PlaceholderImage');
      this.galleryService
        .removeImage(this.getImageModel()?.id);
      await this.reset();
      if (choice === Choice.RETRY) {
        this.clickEvent();
      }
    }
    else {
      // If placeholder image not reachable
      this.element.load(Utils.url(StageConstants.PIXEL_WHITE));
    }
  }

  public async reset(imageUrl?: string): Promise<void>
  {
    await super.reset();
    this.set(StageConstants.ATTRIBUTE_QUALITY_RATING, undefined);

    const galleryImage = this.getImageModel();
    if (galleryImage) {
      this.galleryService.setImageAsUnused(galleryImage.id);
      this.set(StageConstants.ATTRIBUTE_IMAGE_MODEL, undefined);
    }

    const cleared = imageUrl === undefined;
    return new Promise<void>((resolve) => {
      this.element.loaded = async (_imgInfo) => {
        const width = this.element.width();
        const height = this.element.height();
        await this.loaded({ width, height }, true);
        if (cleared) {
          this.galleryService.setSelectedImageId(undefined);
        }
        resolve();
      };

      imageUrl = imageUrl ? imageUrl :
          this.get(StageConstants.ATTRIBUTE_PLACEHOLDER_URL);
      this.element.load(imageUrl);
    });
  }

  public getQualityRating(): number | undefined
  {
    const rating =
        parseInt(this.get(StageConstants.ATTRIBUTE_QUALITY_RATING), 10);

    return isNaN(rating) ? undefined : rating;
  }

  public async restoreGalleryImageIfNecessary(): Promise<void>
  {
    let model = this.getImageModel();

    if (!model) {
      // TODO: Remove me at the earliest after 2026-06-01
      if (this.get('pap:gallery-image-id') !== undefined) {
        model = await this.tryGenerateModel(this.getHref());
      }
      if (!model) {
        return;
      }
    }

    if (!this.galleryService.getImageById(model.id)) {
      await this.galleryService.addImage(model);
    }
    this.galleryService.setImageAsUsed(model.id);
  }

  private async tryGenerateModel(url: string): Promise<GalleryImage|undefined>
  {
    if (!url || url === this.get(StageConstants.ATTRIBUTE_PLACEHOLDER_URL)) {
      return undefined;
    }

    try {
      const info = await this.imageStoreService.getImageInfo(url);
      const model =
          await importImageStoreImage(info)('im' + Utils.getRandomId(24));
      this.setImageModel(model);
      return model;
    }
    catch (error) {
      console.error(error);
      return undefined;
    }
  }

  public async loadLocalImage(input: HTMLInputElement): Promise<void>
  {
    await this.imageProvisioningService.addUserFileToPlaceholder(
        input, this.element);
    this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
    this.placeholderService.setFocusedPlaceholder(undefined);
  }

  public async loadGalleryImage(
    galleryImageId: string, retries = 3): Promise<void>
  {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const galleryImage = this.galleryService.getImageById(galleryImageId);

        // We need to ensure the placeholder is pending, to avoid parallel
        // critical user actions.
        this.set(StageConstants.ATTRIBUTE_PENDING, galleryImage.id);

        const previousModel = this.getImageModel();

        const loadTransaction = this.startTransaction(() => {
          this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
        });

        let url = galleryImage.url;
        const pipeline = this.get(StageConstants.ATTRIBUTE_TRAUDL_PIPELINE);
        if (pipeline) {
          url = galleryImage.traudlUrls[pipeline];
          if (!url) {
            try {
              url = await this.traudlService.loadPipelinedUrl(
                  pipeline, galleryImage.url);
              galleryImage.traudlUrls[pipeline] = url;
            }
            catch (error) {
              this.errorService.add(error, 'PlaceholderImage');
              const choice = await this.errorService.flush('PlaceholderImage');
              loadTransaction.rollback();
              if (choice === Choice.CONTINUE) {
                this.clickEvent();
              }
              else if (choice === Choice.RETRY) {
                await this.loadGalleryImage(galleryImageId);
              }
              resolve();
              return;
            }
          }
        }

        if (previousModel) {
          this.galleryService.setImageAsUnused(previousModel.id);
        }

        this.setImageModel(galleryImage);
        this.galleryService.setImageAsUsed(galleryImage.id);
        await this.commitContent();

        if (!Utils.isLargeDevice() &&
            this.toolboxService.getActiveToolboxName('design-step') ===
                'GalleryToolbox') {
          this.toolboxService.cleanUp('GalleryToolbox');
        }

        this.element.loaded = async () => {
          try {
            this.loaded(galleryImage, true);
            loadTransaction.commit();
            await this.commitContent();
          }
          finally {
            this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
          }
          resolve();
        };

        this.element.off('error');
        this.element.on('error', async (error) => {
          try {
            if (pipeline) {
              delete galleryImage.traudlUrls[pipeline];
              this.setImageModel(galleryImage);
            }
            if (retries > 0) {
              await this.loadGalleryImage(galleryImageId, retries - 1);
            } else {
              this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
              await this.error(error);
            }
          }
          finally {
            this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
          }
          resolve();
        });

        loadUrl(this.element, url);
      }
      catch (error) {
        this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
        throw error;
      }
    });
  }

  private setImageModel(galleryImage: GalleryImage): void
  {
    this.set(
        StageConstants.ATTRIBUTE_IMAGE_MODEL, JSON.stringify(galleryImage));
  }

  public getImageModel(): GalleryImage | undefined
  {
    if (this.get(StageConstants.ATTRIBUTE_IMAGE_MODEL) === undefined) {
      return undefined;
    }
    return this.parseImageModel(this.get(StageConstants.ATTRIBUTE_IMAGE_MODEL));
  }

  private parseImageModel(modelString: string): GalleryImage
  {
    let model = JSON.parse(modelString);
    if (model.expirationDate) {
      model.expirationDate = new Date(model.expirationDate);
    }
    return model;
  }

  public getHref(): string
  {
    return this.get(StageConstants.ATTRIBUTE_HREF)
        || this.element.node.getAttributeNS(
            'http://www.w3.org/1999/xlink', 'href');
  }

  public getDimensions(): undefined|Dimensions
  {
    if (!this.get(StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS)) {
      return undefined;
    }
    return JSON.parse(
        this.get(StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS)
    ) as Dimensions;
  }

  public getBoundingBox(): undefined|BoundingBox
  {
    if (!this.get(StageConstants.ATTRIBUTE_BOUNDING_BOX)) {
      return undefined;
    }
    return JSON.parse(
        this.get(StageConstants.ATTRIBUTE_BOUNDING_BOX)
    ) as BoundingBox;
  }

  public getDisplayedBoundingBox(): undefined|BoundingBox
  {
    const bbox = this.getBoundingBox();

    if (!bbox || this.zoomMode !== StageConstants.ZOOM_MODE_CUT) {
      return bbox;
    }

    const wrapperNode = this.wrapper.node as SVGSVGElement;
    const displayed = Region.centeredFit(
        Region.fromSimilar(bbox),
        wrapperNode.width.baseVal.value / wrapperNode.height.baseVal.value);

    return Math.abs(displayed.width - bbox.width) < 1 &&
           Math.abs(displayed.height - bbox.height) < 1
        ? bbox
        : displayed;
  }

  public applyBoundingBox(
      bbox: BoundingBox, matrix?: DOMMatrixReadOnly): void
  {
    if (!bbox || !this._transformationManager) {
      return;
    }

    if (matrix === undefined) {
      this._transformationManager.applyBoundingBox(bbox);
    } else {
      this._transformationManager.reset(this.getDimensions(), bbox, matrix);
    }

    this.applyCurrentMatrix();

    this.wrapper.viewbox(bbox);
    this.set(StageConstants.ATTRIBUTE_BOUNDING_BOX, JSON.stringify(bbox));

    (<any>this.element).scheduleReload();
    this.changed.emit(this.element.id());
  }

  public approximateEditableArea(): BoundingBox
  {
    const doc = StageUtils.getPartDocument(this.wrapper);
    const constraints = (doc as any).viewbox();

    const wrapperViewBox = this.wrapper.viewbox();
    const boundsRect = (this.wrapper
                        .rect()
                        .move(wrapperViewBox.x, wrapperViewBox.y)
                        .size(wrapperViewBox.width, wrapperViewBox.height));
    const bounds = (boundsRect as any).rbox(doc);
    boundsRect.remove();

    const cx = constraints.x;
    const cy = constraints.y;
    const cw = constraints.width;
    const ch = constraints.height;

    const bx = bounds.x;
    const by = bounds.y;
    const bw = bounds.width;
    const bh = bounds.height;

    const margin = {
      left: cx - bx,
      top: cy - by,
      right: (bx + bw) - (cx + cw),
      bottom: (by + bh) - (cy + ch),
    };

    const normalizedMargin = {
      left: Math.max(margin.left, 0) / bounds.width,
      top: Math.max(margin.top, 0) / bounds.height,
      right: Math.max(margin.right, 0) / bounds.width,
      bottom: Math.max(margin.bottom, 0) / bounds.height,
    };

    return {
      x: normalizedMargin.left * 100,
      y: normalizedMargin.top * 100,
      width: 100 - (normalizedMargin.right + normalizedMargin.left) * 100,
      height: 100 - (normalizedMargin.top + normalizedMargin.bottom) * 100,
    };
  }

  private updateQualityRating(): void
  {
    if (!this.isUnchanged()) {
      this.imageQualityRatingService.readQualityRating(this);
    }
  }

  private setBoxSize(group: G): number
  {
    const box = this.wrapper.viewbox();
    const boxSize = Math.min(box.width, box.height) / 4;

    const ctm = group.node.getScreenCTM();

    if (ctm) {
      const pixelScale = Math.hypot(ctm.a, ctm.b);
      const maxBoxSize = ImagePlaceholder.MAX_BOX_SIZE / pixelScale;
      return Math.min(maxBoxSize, boxSize);
    }
    else {
      return boxSize;
    }
  }

  private get placeholderHighlighterImagePath(): string
  {
    if (!this.isUnchanged()) {
      return this.REPLACE_HIGHLIGHTER_IMAGE_PATH;
    }
    else {
      return this.ADD_HIGHLIGHTER_IMAGE_PATH;
    }
  }

  public showPlaceholderHighlighter(): void
  {
    if (!this.placeholderHighlighter) {
      const box = this.wrapper.viewbox();

      const placeholderHighlighterGroup = this.wrapper.group();

      const size = this.setBoxSize(placeholderHighlighterGroup);

      const circle = placeholderHighlighterGroup.circle(size)
        .addClass('highlighter-circle')
        .stroke({ width: size * 0.02 });

      placeholderHighlighterGroup.image()
        .size(size * 0.4, size * 0.4)
        .center(circle.cx(), circle.cy())
        .load(this.placeholderHighlighterImagePath);

      placeholderHighlighterGroup
        .addClass(StageConstants.CLASS_NAME_PLACEHOLDER_HIGHLIGHTER)
        .center(box.x + box.width / 2, box.y + box.height / 2);

      this.placeholderHighlighter = placeholderHighlighterGroup;
    }
  }

  public hidePlaceholderHighlighter(): void
  {
    if (this.placeholderHighlighter) {
      this.placeholderHighlighter.remove();
      this.placeholderHighlighter = undefined;
      this.galleryService.setSelectedImageId(undefined);
    }
  }

  private async imageClicked(event: MouseEvent): Promise<void>
  {
    const focused = this.placeholderService.getFocusedPlaceholder();
    if (focused && focused.type === PlaceholderType.Text) {
      event.stopPropagation();
      this.placeholderService.setFocusedPlaceholder(undefined);
      return;
    }

    await this.clickEvent(event);
  }

  protected async handleClick(): Promise<void>
  {
    if (this.hasEditableContent()) {
      this.toolboxService.open('ImageEditToolbox');
    }
  }

  private get dragDropGhostSwapIcon(): HTMLElement
  {
    return this.rootNode.querySelector('.dragdrop-ghost-swap-icon');
  }

  private showDragDropGhostSwapIcon(): void
  {
    const swapIcon = this.dragDropGhostSwapIcon;

    if (!swapIcon) {
      return;
    }

    if (this.isUnchanged() ||
        this.galleryService.getDraggingImageId() !==
        this.getImageModel().id) {
      swapIcon.style.display = 'block';
    }
  }

  private hideDragDropGhostSwapIcon(): void
  {
    const swapIcon = this.dragDropGhostSwapIcon;

    if (!swapIcon) {
      return;
    }

    swapIcon.style.display = 'none';
  }

  public showImageOverlay(): void
  {
    if (this.imageOverlayShown) {
      return;
    }

    const root = this.wrapper.root();

    const wrapperRootChild = StageUtils.findRootChildParent(this.wrapper);
    this.sourcePrevSibling = wrapperRootChild.node.previousElementSibling;

    const designOnlyNode = Array.from(root.node.children).find(
        (child) => child.classList.contains(
            StageConstants.GROUP_TAG_DESIGN_ONLY));

    if (designOnlyNode) {
      root.node.insertBefore(wrapperRootChild.node, designOnlyNode.nextSibling);
    } else {
      root.node.appendChild(wrapperRootChild.node);
    }

    this.clipPath = StageUtils.findClipPath(this.group);

    this.wrapper.node.style.overflow = 'visible';

    const mask = root.mask().id(this.wrapper.id() + '-overlay-mask')
      .addClass(StageConstants.CLASS_NAME_IMAGE_OVERLAY);
    mask
      .rect(ImagePlaceholder.OVERLAY_MASK_SIZE,
            ImagePlaceholder.OVERLAY_MASK_SIZE)
      .move(ImagePlaceholder.OVERLAY_MASK_SIZE / -2,
            ImagePlaceholder.OVERLAY_MASK_SIZE / -2)
      .fill('white');

    const visibleArea =
        StageUtils.getWrapperRegionWithinPartViewBox(this.wrapper);

    const blackMask = mask.rect(visibleArea.width, visibleArea.height)
                          .move(visibleArea.x, visibleArea.y)
                          .fill('black');

    const group = new G();
    group.insertAfter(this.group);
    group.id(this.wrapper.id() + '-overlay-rect');
    group.addClass(StageConstants.CLASS_NAME_IMAGE_OVERLAY);
    const container = this.wrapper.parent().clone().clear() as G;
    container
      .rect(ImagePlaceholder.OVERLAY_MASK_SIZE,
            ImagePlaceholder.OVERLAY_MASK_SIZE)
      .move(ImagePlaceholder.OVERLAY_MASK_SIZE / -2,
            ImagePlaceholder.OVERLAY_MASK_SIZE / -2)
      .fill('rgba(0, 0, 0, 0.5)')
      .maskWith(mask)
      .attr('pointer-events', 'none');

    if (this.clipPath) {
      this.group.unclip();
      container.unclip();
    }

    container.addTo(group);

    const viewbox = root.viewbox();

    // Map the stage viewbox from root into the element's local coordinate
    // system (as polygon points), so the border filter and mask hole can be
    // clipped to prevent border filter overflow.
    const stagePolygon = (el: SvgElement): string => {
      const elNode = el.node as SVGGraphicsElement;
      const rootToLocal = elNode.getScreenCTM().inverse()
                            .multiply(root.node.getScreenCTM());

      const toLocalPoint = (x: number, y: number): string =>
          `${rootToLocal.a * x + rootToLocal.c * y + rootToLocal.e},` +
          `${rootToLocal.b * x + rootToLocal.d * y + rootToLocal.f}`;

      return [
        toLocalPoint(viewbox.x, viewbox.y),
        toLocalPoint(viewbox.x + viewbox.width, viewbox.y),
        toLocalPoint(viewbox.x + viewbox.width, viewbox.y + viewbox.height),
        toLocalPoint(viewbox.x, viewbox.y + viewbox.height),
      ].join(' ');
    };

    const borderFilterGroup = group.next();

    const overlayClip = new ClipPath().id(this.wrapper.id() + '-overlay-clip')
      .addClass(StageConstants.CLASS_NAME_IMAGE_OVERLAY);
    root.defs().add(overlayClip);
    overlayClip.polygon(stagePolygon(borderFilterGroup));
    borderFilterGroup.clipWith(overlayClip);

    const maskClip =
        new ClipPath().id(this.wrapper.id() + '-overlay-mask-clip')
          .addClass(StageConstants.CLASS_NAME_IMAGE_OVERLAY);
    root.defs().add(maskClip);
    maskClip.polygon(stagePolygon(container));
    if (this.clipPath) {
      maskClip.clipWith(this.clipPath);
    }
    blackMask.clipWith(maskClip);

    const overlayPartDoc = StageUtils.getPartDocument(this.wrapper);
    if (overlayPartDoc &&
        overlayPartDoc.attr(StageConstants.ATTRIBUTE_PART_ID)) {
      this.relocateOverlayToStageRoot(root, [group, borderFilterGroup]);
    }

    this.imageOverlayShown = true;
  }

  public hideImageOverlay(): void
  {
    if (!this.imageOverlayShown) {
      return;
    }
    this.imageOverlayShown = false;

    this.restoreOverlayFromStageRoot();

    const root = this.wrapper.root();
    const wrapperRootChild = StageUtils.findRootChildParent(this.wrapper);
    root.node.insertBefore(
        wrapperRootChild.node, this.sourcePrevSibling.nextElementSibling);
    this.wrapper.node.style.overflow = 'hidden';

    if (this.clipPath) {
      this.group.clipWith(this.clipPath);
    }

    root.node.querySelectorAll(
        '[id^="' + this.wrapper.id() + '-overlay-"]')
        .forEach(el => el.remove());

    root.node.querySelectorAll(
        '[clip-path*="' + this.wrapper.id() + '-overlay-"]')
        .forEach(el => el.removeAttribute('clip-path'));

  }

  private relocateOverlayToStageRoot(
      root: Svg, siblingsOfGroup: Array<SvgElement>): void
  {
    const parent = this.group && this.group.node.parentNode;
    if (!parent) {
      return;
    }

    const parentCTM = (parent as SVGGraphicsElement).getScreenCTM();
    const rootCTM = (root.node as SVGGraphicsElement).getScreenCTM();
    if (!parentCTM || !rootCTM) {
      return;
    }

    const parentToRoot = rootCTM.inverse().multiply(parentCTM);
    const container = root.group().attr(
        'transform',
        `matrix(${parentToRoot.a},${parentToRoot.b},${parentToRoot.c},` +
        `${parentToRoot.d},${parentToRoot.e},${parentToRoot.f})`);

    this.overlayRelocation = {
      container,
      parent,
      prevSibling: this.group.node.previousSibling,
    };

    container.node.append(
        this.group.node,
        ...siblingsOfGroup.filter(el => !!el).map(el => el.node));
  }

  private restoreOverlayFromStageRoot(): void
  {
    if (!this.overlayRelocation) {
      return;
    }

    const { container, parent, prevSibling } = this.overlayRelocation;
    const insertionPoint =
        prevSibling ? prevSibling.nextSibling : parent.firstChild;

    for (const node of Array.from(container.node.childNodes)) {
      parent.insertBefore(node, insertionPoint);
    }

    container.remove();
    this.overlayRelocation = undefined;
  }

  public hideQualityIndication(): void
  {
    if (this.qualityIndicatorGroup) {
      this.qualityIndicatorGroup.remove();
      this.qualityIndicatorGroup = undefined;
    }
  }

  public showQualityIndication(): void
  {
    const rating = this.getQualityRating();
    this.hideQualityIndication();

    if (this.isUnchanged() || rating === undefined) {
      return;
    }

    const qualityIndicatorGroup = this.wrapper.group()
      .addClass(StageConstants.CLASS_NAME_QUALITY_INDICATION);

    const background = qualityIndicatorGroup.rect();
    const circle = qualityIndicatorGroup.circle()
      .addClass('highlighter-circle');
    const icon = qualityIndicatorGroup.image();

    const indicator = QualityRating.getQualityIndicator(rating);
    const box = this.wrapper.viewbox();

    const size = this.setBoxSize(qualityIndicatorGroup);

    background
      .size(box.width, box.height)
      .attr('class', indicator.backgroundClass);

    circle
      .size(size)
      .stroke({ width: size * 0.02 })
      .center(background.cx(),
              background.cy());

    icon
      .size(size * 0.8, size * 0.8)
      .center(circle.cx(),
              circle.cy())
      .load(indicator.imageUrl);

    qualityIndicatorGroup
      .center(box.x + box.width / 2, box.y + box.height / 2);

    this.qualityIndicatorGroup = qualityIndicatorGroup;
  }
}
